import { useState } from 'react'

import { Tooltip } from '@mantine/core'

import DNButton from 'src/components/Buttons/Button/DNButton'
import {
  SearchBox,
  SearchBoxProps,
} from 'src/components/Inputs/SearchBox/SearchBox'
import IconSearch from 'src/icons/IconSearch'

export interface CollapsibleSearchBoxProps
  extends Omit<
    SearchBoxProps,
    'className' | 'focusOnMount' | 'onFocus' | 'onBlur' | 'onEscape'
  > {
  /** The term currently applied to results, as opposed to the text being typed. */
  appliedValue: string
  expandedClassName?: string
}

export const CollapsibleSearchBox = ({
  value,
  setValue,
  onSearch,
  appliedValue,
  placeholder = '',
  size,
  ariaLabel = 'Search',
  expandedClassName = 'w-[260px]',
}: CollapsibleSearchBoxProps) => {
  // `open` tracks "the user opened it or it has focus"; the box also stays
  // mounted while it holds text so an applied search is always visible.
  const [open, setOpen] = useState(false)
  const [openedByClick, setOpenedByClick] = useState(false)
  const expanded = open || value.length > 0
  const hasAppliedSearch = appliedValue.length > 0

  const close = () => {
    setOpen(false)
    setOpenedByClick(false)
    if (!value && hasAppliedSearch) onSearch('')
  }

  if (!expanded) {
    return (
      <Tooltip label={ariaLabel}>
        <DNButton
          variant="outline"
          aria-label={ariaLabel}
          onClick={() => {
            setOpen(true)
            setOpenedByClick(true)
          }}
        >
          <IconSearch className="h-4 w-4 fill-none stroke-doubleNickel-gray-700" />
        </DNButton>
      </Tooltip>
    )
  }

  return (
    <SearchBox
      value={value}
      setValue={setValue}
      onSearch={onSearch}
      placeholder={placeholder}
      size={size}
      ariaLabel={ariaLabel}
      className={expandedClassName}
      focusOnMount={openedByClick}
      onFocus={() => setOpen(true)}
      onBlur={close}
      onEscape={() => {
        if (value) setValue('')
        setOpen(false)
        setOpenedByClick(false)
        if (hasAppliedSearch) onSearch('')
      }}
    />
  )
}

export default CollapsibleSearchBox
